import { useState, useCallback, useMemo, useEffect } from 'react'
import { Box } from '@mui/material'
import { useTheme } from '@/contexts/ThemeContext'
import { useTreasuryBalance } from '@/contexts/TreasuryBalanceContext'
import { usePublicClient, useWaitForTransactionReceipt } from 'wagmi'
import { getEnv } from '@/config/env'
import STNGImplementationAbi from '@/abis/STNGImplementation.json'
import { useNotification } from '@/hooks/useNotification'
import { parseUnits } from 'viem'
import type { Abi } from 'viem'
import { isUserRejectedError } from '@/utils/errorHandler'
import TransactionCancelledModal from '../TransactionCancelledModal'
import useSafe from '@/localsafe.eth/hooks/useSafe'
import { useChainId } from 'wagmi'
import { submitSafeTransactionToService } from '@/utils/safeTransactionService'
import { getAddress } from 'viem'
import { DEFAULT_TOKEN_DECIMALS } from '@/constants/main'
import { isValidEthereumAddress, formatHash } from '@/utils/formatting'
import {
  buildSafeTransactionParams,
  encodeContractCall,
} from '@/utils/safeTransactionHelpers'
import { createCardStyles } from '@/components/shared/commonStyles'
import SectionHeader from '@/components/shared/SectionHeader'
import MintBurnForm from './Actions/MintBurnForm'
import { useWallet } from '@/hooks/useWallet'

export default function Actions() {
  const [amount, setAmount] = useState<string>('')
  const [recipient, setRecipient] = useState<string>('')
  const [recipientError, setRecipientError] = useState<string>('')
  const [amountError, setAmountError] = useState<string>('')
  const [isMinting, setIsMinting] = useState(false)
  const [isBurning, setIsBurning] = useState(false)
  const [decimals, setDecimals] = useState<number>(DEFAULT_TOKEN_DECIMALS)
  const [showCancelledModal, setShowCancelledModal] = useState(false)
  const [cancelledMessage, setCancelledMessage] = useState<string>('')
  const [mintTxHash, setMintTxHash] = useState<`0x${string}` | null>(null)
  const [burnTxHash, setBurnTxHash] = useState<`0x${string}` | null>(null)
  const { showSuccess, showError } = useNotification()
  const { colors } = useTheme()
  const { treasuryBalance } = useTreasuryBalance()

  const { stngProxy, treasurySafe } = useMemo(() => getEnv(), [])
  const publicClient = usePublicClient()
  const chainId = useChainId()
  const { address: userAddress } = useWallet()

  const {
    isReady: isSafeReady,
    buildSafeTransaction,
    signSafeTransaction,
    executeSafeTransaction,
    getSafeTransactionHash,
    isTransactionReadyToExecute,
  } = useSafe(treasurySafe as `0x${string}`)

  useEffect(() => {
    const fetchDecimals = async () => {
      if (!publicClient || !stngProxy || !chainId || !publicClient.readContract) {
        return
      }
      try {
        const contractDecimals = (await publicClient.readContract({
          address: stngProxy as `0x${string}`,
          abi: STNGImplementationAbi as Abi,
          functionName: 'decimals',
        })) as number
        setDecimals(contractDecimals)
      } catch (error) {
        console.error('Failed to fetch decimals:', error)
      }
    }
    fetchDecimals()
  }, [publicClient, stngProxy, chainId])

  const { isLoading: isConfirmingMint, isSuccess: isMintSuccess } =
    useWaitForTransactionReceipt({
      hash: mintTxHash && publicClient ? mintTxHash : undefined,
      query: { enabled: !!mintTxHash && !!publicClient && !!chainId },
    })

  const { isLoading: isConfirmingBurn, isSuccess: isBurnSuccess } =
    useWaitForTransactionReceipt({
      hash: burnTxHash && publicClient ? burnTxHash : undefined,
      query: { enabled: !!burnTxHash && !!publicClient && !!chainId },
    })

  useEffect(() => {
    if (isMintSuccess && isMinting && mintTxHash) {
      setIsMinting(false)
      setAmount('')
      setRecipient('')
      setRecipientError('')
      setMintTxHash(null)
      showSuccess('Tokens minted successfully!')
    }
  }, [isMintSuccess, isMinting, mintTxHash, showSuccess])

  useEffect(() => {
    if (isBurnSuccess && isBurning && burnTxHash) {
      setIsBurning(false)
      setAmount('')
      setAmountError('')
      setBurnTxHash(null)
      showSuccess('Tokens burned successfully!')
    }
  }, [isBurnSuccess, isBurning, burnTxHash, showSuccess])

  const handleRecipientChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim()
      setRecipient(value)

      if (value === '') {
        setRecipientError('')
      } else if (!isValidEthereumAddress(value)) {
        setRecipientError('Invalid Ethereum address format')
      } else {
        setRecipientError('')
      }
    },
    []
  )

  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setAmount(value)
      setAmountError('')
    },
    []
  )

  const handleMint = useCallback(async () => {
    if (!amount || !recipient || parseFloat(amount) <= 0) {
      showError('Please enter valid amount and recipient address')
      return
    }

    if (recipientError) {
      showError('Please enter a valid recipient address')
      return
    }

    if (!isSafeReady) {
      showError('Safe is not ready. Please wait and try again.')
      return
    }

    try {
      setIsMinting(true)
      const amountInWei = parseUnits(amount, decimals)

      const data = encodeContractCall(STNGImplementationAbi as Abi, 'mint', [
        getAddress(recipient),
        amountInWei,
      ])

      const safeTx = await buildSafeTransaction([
        buildSafeTransactionParams({
          to: getAddress(stngProxy),
          data,
        }),
      ])

      if (!safeTx) {
        showError('Failed to build Safe transaction')
        setIsMinting(false)
        return
      }

      let signedTx
      try {
        signedTx = await signSafeTransaction(safeTx)

        if (chainId && userAddress) {
          try {
            const submitResult = await submitSafeTransactionToService(
              signedTx,
              treasurySafe,
              chainId,
              userAddress,
              getSafeTransactionHash
            )
            if (!submitResult.success) {
              console.error(
                'Failed to submit transaction to Safe Transaction Service:',
                submitResult.error
              )
              showError(
                `Transaction created but failed to sync with Safe Transaction Service: ${submitResult.error}`
              )
            } else {
              showSuccess(
                'Transaction created and synced with Safe Wallet! Other owners can now sign it.'
              )
            }
          } catch (error) {
            console.error(
              'Error submitting transaction to Safe Transaction Service:',
              error
            )
          }
        }
      } catch (error) {
        setIsMinting(false)
        if (isUserRejectedError(error)) {
          setCancelledMessage(
            'You cancelled the signature request in MetaMask.'
          )
          setShowCancelledModal(true)
        } else {
          showError(
            error instanceof Error
              ? error.message
              : 'Failed to sign Safe transaction'
          )
        }
        return
      }

      const isReady = await isTransactionReadyToExecute(signedTx)

      if (isReady) {
        try {
          const hash = await executeSafeTransaction(signedTx)
          if (hash) {
            setMintTxHash(hash)
            setAmount('')
            setRecipient('')
            setRecipientError('')
            showSuccess('Mint transaction executed successfully!')
          }
        } catch (error) {
          setIsMinting(false)
          if (isUserRejectedError(error)) {
            setCancelledMessage(
              'You cancelled the execution transaction in MetaMask.'
            )
            setShowCancelledModal(true)
          } else {
            const errorMessage =
              error instanceof Error ? error.message : String(error)
            showError(errorMessage || 'Failed to execute mint transaction')
          }
        }
      } else {
        setIsMinting(false)
        setAmount('')
        setRecipient('')
        setRecipientError('')
        getSafeTransactionHash(signedTx)
          .then((txHash) => {
            showSuccess(
              `Mint transaction created and signed! It requires more signatures before execution. Transaction hash: ${formatHash(
                txHash
              )}...`
            )
          })
          .catch(() => {
            showSuccess(
              'Mint transaction created and signed! It requires more signatures before execution.'
            )
          })
      }
    } catch (error) {
      setIsMinting(false)
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to mint tokens. Please try again.'
      )
    }
  }, [
    amount,
    recipient,
    recipientError,
    decimals,
    stngProxy,
    treasurySafe,
    chainId,
    isSafeReady,
    buildSafeTransaction,
    signSafeTransaction,
    executeSafeTransaction,
    getSafeTransactionHash,
    showError,
    showSuccess,
    userAddress,
    isTransactionReadyToExecute,
  ])

  const handleBurn = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) {
      showError('Please enter a valid amount')
      return
    }

    if (!isSafeReady) {
      showError('Safe is not ready. Please wait and try again.')
      return
    }

    if (treasuryBalance === null) {
      showError('Unable to fetch treasury balance. Please try again.')
      return
    }

    const amountInWei = parseUnits(amount, decimals)
    if (amountInWei > treasuryBalance) {
      setAmountError('Amount exceeds treasury balance')
      return
    }

    setAmountError('')

    try {
      setIsBurning(true)

      const data = encodeContractCall(STNGImplementationAbi as Abi, 'burn', [
        amountInWei,
      ])

      const safeTx = await buildSafeTransaction([
        buildSafeTransactionParams({
          to: getAddress(stngProxy),
          data,
        }),
      ])

      if (!safeTx) {
        showError('Failed to build Safe transaction')
        setIsBurning(false)
        return
      }

      let signedTx
      try {
        signedTx = await signSafeTransaction(safeTx)

        if (chainId && userAddress) {
          try {
            const submitResult = await submitSafeTransactionToService(
              signedTx,
              treasurySafe,
              chainId,
              userAddress,
              getSafeTransactionHash
            )
            if (!submitResult.success) {
              console.error(
                'Failed to submit transaction to Safe Transaction Service:',
                submitResult.error
              )
              showError(
                `Transaction created but failed to sync with Safe Transaction Service: ${submitResult.error}`
              )
            } else {
              showSuccess(
                'Transaction created and synced with Safe Wallet! Other owners can now sign it.'
              )
            }
          } catch (error) {
            console.error(
              'Error submitting transaction to Safe Transaction Service:',
              error
            )
          }
        }
      } catch (error) {
        setIsBurning(false)
        if (isUserRejectedError(error)) {
          setCancelledMessage(
            'You cancelled the signature request in MetaMask.'
          )
          setShowCancelledModal(true)
        } else {
          showError(
            error instanceof Error
              ? error.message
              : 'Failed to sign Safe transaction'
          )
        }
        return
      }

      const isReady = await isTransactionReadyToExecute(signedTx)

      if (isReady) {
        try {
          const hash = await executeSafeTransaction(signedTx)
          if (hash) {
            setBurnTxHash(hash)
            setAmount('')
            setAmountError('')
            showSuccess('Burn transaction executed successfully!')
          }
        } catch (error) {
          setIsBurning(false)
          if (isUserRejectedError(error)) {
            setCancelledMessage(
              'You cancelled the execution transaction in MetaMask.'
            )
            setShowCancelledModal(true)
          } else {
            const errorMessage =
              error instanceof Error ? error.message : String(error)
            showError(errorMessage || 'Failed to execute burn transaction')
          }
        }
      } else {
        setIsBurning(false)
        setAmount('')
        setAmountError('')
        getSafeTransactionHash(signedTx)
          .then((txHash) => {
            showSuccess(
              `Burn transaction created and signed! It requires more signatures before execution. Transaction hash: ${formatHash(
                txHash
              )}...`
            )
          })
          .catch(() => {
            showSuccess(
              'Burn transaction created and signed! It requires more signatures before execution.'
            )
          })
      }
    } catch (error) {
      setIsBurning(false)
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to burn tokens. Please try again.'
      )
    }
  }, [
    amount,
    decimals,
    treasuryBalance,
    stngProxy,
    treasurySafe,
    chainId,
    isSafeReady,
    buildSafeTransaction,
    signSafeTransaction,
    executeSafeTransaction,
    getSafeTransactionHash,
    showError,
    showSuccess,
    userAddress,
    isTransactionReadyToExecute,
  ])

  return (
    <Box sx={{ mb: 4 }}>
      <SectionHeader title="Actions" />
      <Box
        sx={{
          ...createCardStyles(colors),
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <MintBurnForm
          recipient={recipient}
          recipientError={recipientError}
          amount={amount}
          amountError={amountError}
          onRecipientChange={handleRecipientChange}
          onAmountChange={handleAmountChange}
          onMint={handleMint}
          onBurn={handleBurn}
          isMinting={isMinting}
          isBurning={isBurning}
          isConfirmingMint={isConfirmingMint}
          isConfirmingBurn={isConfirmingBurn}
          isSafeReady={isSafeReady}
        />
      </Box>
      <TransactionCancelledModal
        open={showCancelledModal}
        onClose={() => {
          setShowCancelledModal(false)
          setCancelledMessage('')
        }}
        message={cancelledMessage}
      />
    </Box>
  )
}
